import { useState, ReactNode, Fragment, useRef } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Checkbox from '../components/Checkbox'
import FormResult from '../components/FormResult'
import FormFieldDate from '../components/FormFieldDate'
import FormFieldRadioGroup from '../components/FormFieldRadioGroup'
import FormFieldSelect from '../components/FormFieldSelect'
import FormFieldText from '../components/FormFieldText'
import RecaptchaLoader from '../components/RecaptchaLoader'
import RecaptchaContainer from '../components/RecaptchaContainer'
import type { FormFieldDateProps } from '../components/FormFieldDate'
import type { FormFieldRadioGroupProps } from '../components/FormFieldRadioGroup'
import type { FormFieldSelectProps } from '../components/FormFieldSelect'
import type { FormFieldTextProps } from '../components/FormFieldText'
import Stepper from '../components/Stepper'
import { primaryButtonCss, invisibleButtonCss } from '../components/Button'
import ChevronRight from '../icons/ChevronRight.svg'
import { useRecaptchaForm } from '../utils/useRecaptchaForm'

const ChevronLeft = styled(ChevronRight)(
  ({ theme: { breakpoints } }) => css`
    font-size: 20px;
    transform: rotateZ(180deg);
    vertical-align: bottom;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      font-size: 24px;
    }
  `,
)
const Section = styled.section(
  ({ theme }) => css`
    padding: ${theme.spacing.x5}px ${theme.spacing.x2}px;

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      padding: ${theme.spacing.x10}px ${theme.spacing.x2}px;
    }
  `,
)
const Form = styled.form(
  ({ theme }) => css`
    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      margin: 0 auto;
      max-width: ${theme.spacing.gridMaxWidth}px;
    }
  `,
)
const FormSection = styled.section<{ active: boolean }>(
  ({ active, theme: { breakpoints, spacing } }) => css`
    display: ${active ? 'block' : 'none'};
    padding-top: ${spacing.x5}px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      max-width: 552px;
    }
  `,
)
const SubmitSection = styled.section(
  ({ theme: { breakpoints } }) => css`
    @media screen and (min-width: ${breakpoints.tablet}px) {
      max-width: 552px;
    }
  `,
)
const NextButton = styled.button(
  ({ theme }) => css`
    ${primaryButtonCss(theme)}
    margin-top: ${theme.spacing.x2}px;
  `,
)
const PreviousButton = styled.button(
  ({ theme: { breakpoints, spacing } }) => css`
    ${invisibleButtonCss()}
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
    line-height: ${spacing.x3}px;
    margin-right: ${spacing.x3}px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      font-size: 16px;
      font-weight: normal;
      line-height: ${spacing.x3 + spacing.x0}px;
    }
  `,
)
const Recaptcha = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.neutral1};
    font-size: 11px;
    line-height: 1.45;
    padding: ${theme.spacing.x2}px;
    margin-bottom: ${theme.spacing.x3}px;

    p {
      margin: 0;
    }

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      font-size: 14px;
      line-height: 1.57;
      margin-bottom: ${theme.spacing.x3}px;
      padding: ${theme.spacing.x3}px;
    }
  `,
)
const Privacy = styled.label(
  ({ theme }) =>
    css`
      align-items: center;
      cursor: pointer;
      display: flex;
      font-size: 12px;
      margin-bottom: ${theme.spacing.x2}px;

      @media screen and (min-width: ${theme.breakpoints.tablet}px) {
        font-size: 16px;
        margin-bottom: ${theme.spacing.x3}px;
      }
    `,
)
const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== 'error',
})<{ error?: boolean }>(
  ({ error, theme }) => css`
    border: 1px solid
      ${error ? `${theme.colors.error}` : `${theme.colors.neutral2}`};
    margin-right: 12px;
  `,
)
const Submit = styled.button(
  ({ theme }) => css`
    ${primaryButtonCss(theme)}
    font-weight: bold;
    line-height: 1.57;
    padding: ${theme.spacing.x2}px;
  `,
)
const RequiredLabel = styled.div(
  ({ theme: { colors, spacing } }) => css`
    margin-bottom: ${spacing.x5}px;

    span {
      color: ${colors.error};
    }
  `,
)

interface FieldText extends FormFieldTextProps {
  __typename: 'FormFieldTextRecord'
  controlledBy?: string | null
  controlledByValues?: string | null
}

interface FieldRadioGroup extends FormFieldRadioGroupProps {
  __typename: 'FormFieldRadioGroupRecord'
  controlledBy?: string | null
  controlledByValues?: string | null
}

interface FieldSelect extends FormFieldSelectProps {
  __typename: 'FormFieldSelectRecord'
  controlledBy?: string | null
  controlledByValues?: string | null
}

interface FieldDate extends FormFieldDateProps {
  __typename: 'FormFieldDateRecord'
  controlledBy?: string | null
  controlledByValues?: string | null
}

interface Props {
  formId: string
  name?: string | null
  recaptcha: ReactNode
  privacyPolicy: ReactNode
  error?: {
    title?: string
    text?: string
    buttonText?: string
  }
  success?: {
    title?: string
    text?: string
    link: {
      text?: string
      href: string
      target?: string
    }
  }
  submitText?: string | null
  nextText?: string | null
  previousText?: string | null
  formTabs: {
    id: string
    title: string
    requiredText: string
    formFields: Array<FieldText | FieldRadioGroup | FieldSelect | FieldDate>
  }[]
}

interface IFormInputs {
  [key: string]: string | boolean | number
}

const isFieldActive = (value: string, values: string[]) =>
  values?.includes(value)

const FormTabsSection = ({
  formId,
  recaptcha,
  privacyPolicy,
  error,
  success,
  submitText,
  nextText,
  previousText,
  formTabs,
}: Props) => {
  const [active, setActive] = useState(0)
  const {
    recaptchaElementId,
    isLoading,
    getRecaptchaToken,
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useRecaptchaForm<IFormInputs>()
  const sectionRef = useRef<HTMLElement | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [greatSuccess, setGreatSuccess] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formError, setFormError] = useState(false)

  const onSubmit = async (data: IFormInputs) => {
    setIsSubmitting(true)

    try {
      const recaptchaResponse = await getRecaptchaToken()

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formId,
          fields: data,
          ...recaptchaResponse,
        }),
      })
      const result = await response.json()

      if (!result.success) {
        setFormError(true)
        return
      }
      setGreatSuccess(true)
    } catch (err) {
      setFormError(true)
      throw err
    } finally {
      setIsSubmitting(false)
      setActive(active + 1)
    }
  }

  if (greatSuccess) {
    return (
      <Section>
        <Stepper
          active={active}
          steps={formTabs.map(({ id, title }) => ({
            id,
            title,
          }))}
        />
        <FormSection active>
          <FormResult type="success" {...success} />
        </FormSection>
      </Section>
    )
  }

  if (formError) {
    return (
      <Section>
        <Stepper
          active={active}
          steps={formTabs.map(({ id, title }) => ({
            id,
            title,
          }))}
        />
        <FormSection active>
          <FormResult
            type="error"
            {...error}
            onClick={() => {
              setActive(active - 1)
              setFormError(false)
            }}
          />
        </FormSection>
      </Section>
    )
  }

  return (
    <Section ref={sectionRef}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Stepper
          active={active}
          onClick={(step) => setActive(step)}
          steps={formTabs.map(({ id, title }) => ({
            id,
            title,
          }))}
        />
        {formTabs.map(({ id, requiredText, formFields }, index) => {
          const splitText = requiredText.split('*')
          return (
            <FormSection id={id} key={id} active={active === index}>
              <RequiredLabel>
                {splitText.map((text, index) =>
                  index === splitText.length - 1 ? (
                    <Fragment key={text}>{text}</Fragment>
                  ) : (
                    <Fragment key={text}>
                      {text}
                      <span>*</span>
                    </Fragment>
                  ),
                )}
              </RequiredLabel>
              {formFields.map(
                ({
                  __typename,
                  controlledBy,
                  controlledByValues,
                  id,
                  ...rest
                }) => {
                  if (
                    controlledBy === '' ||
                    (controlledBy &&
                      controlledByValues &&
                      isFieldActive(
                        watch(controlledBy) as string,
                        controlledByValues.split('\n'),
                      ))
                  ) {
                    const props = {
                      ...rest,
                      errors,
                      id,
                      register,
                      trigger,
                    }
                    switch (__typename) {
                      case 'FormFieldTextRecord':
                        return <FormFieldText {...props} key={id} />
                      case 'FormFieldRadioGroupRecord':
                        return <FormFieldRadioGroup {...props} key={id} />
                      case 'FormFieldSelectRecord':
                        return <FormFieldSelect {...props} key={id} />
                      case 'FormFieldDateRecord':
                        return <FormFieldDate {...props} key={id} />
                      default:
                        return null
                    }
                  }
                  return null
                },
              )}
              {active > 0 && active < formTabs.length - 1 && (
                <PreviousButton
                  onClick={() => {
                    setActive(active - 1)
                    requestAnimationFrame(() =>
                      sectionRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'start',
                      }),
                    )
                  }}
                  type="button"
                >
                  <ChevronLeft /> {previousText}
                </PreviousButton>
              )}
              {active < formTabs.length - 1 && (
                <NextButton
                  onClick={async () => {
                    const valid = await trigger(
                      formFields.map(({ name }) => name as string),
                    )
                    if (valid) {
                      setActive(active + 1)
                      requestAnimationFrame(() =>
                        sectionRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                          inline: 'start',
                        }),
                      )
                    }
                  }}
                  type="button"
                >
                  {nextText} <ChevronRight />
                </NextButton>
              )}
            </FormSection>
          )
        })}
        {active === formTabs.length - 1 && (
          <SubmitSection>
            <Recaptcha>{recaptcha}</Recaptcha>
            <RecaptchaLoader />
            <RecaptchaContainer id={recaptchaElementId} />
            <Privacy>
              <StyledCheckbox
                {...register('privacyPolicy', { required: true })}
                as="div"
                type="checkbox"
                error={Boolean(errors.privacyPolicy)}
              />
              {privacyPolicy}
            </Privacy>
            <Submit disabled={isSubmitting || isLoading} type="submit">
              {submitText}
              <ChevronRight />
            </Submit>
          </SubmitSection>
        )}
      </Form>
    </Section>
  )
}

export default FormTabsSection
